import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";

import "./VideoPlayer.scss";
import VideoControls from "./VideoControls";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { hexToRGBA } from "../../helpers/ColorHelpers";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { Flags } from "../../generated/FeatureFlags";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import VideoPlayer from "./VideoPlayer";
import { SegmentationService } from "../../services/segmentation/SegmentationService";

interface VideoPlayerProps {
  videoSegmentation?: SegmentationService;
}

export default observer(function VideoAnalysisPlayer(props: VideoPlayerProps) {
  const {
    isFullscreen,
    videoProgress,
    showArea,
    showBox,
    showContour,
    playerWidth: videoPlayerWidth,
    playerHeight: videoPlayerHeight,
    setWidth: setVideoPlayerWidth,
    setHeight: setVideoPlayerHeight
  } = videoPlayerService;

  const requestId = useRef<number>();

  const { videoSegmentation } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const filteredSegmentations = useMemo(() => {
    if (videoSegmentation?.isLoading || !videoSegmentation?.segmentationSuccessful) return [];

    if (
      videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended ||
      videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Individual
    ) {
      let defaultSegmentations: Segmentation[] = [];

      const confidenceLevels =
        videoSegmentation?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended
          ? videoSegmentation?.vaData.recommendedConfidenceLevels
          : videoSegmentation?.detectionIndividualConfidenceLevels;

      for (const groupCode in videoSegmentation?.detectedGroupCodes) {
        defaultSegmentations = [
          ...defaultSegmentations,
          ...videoSegmentation?.vaData.segmentationDetails?.segmentations.filter((x) => {
            return (
              x.groupCode === videoSegmentation?.detectedGroupCodes[groupCode].code &&
              x.accuracy === confidenceLevels[videoSegmentation?.detectedGroupCodes[groupCode].code]
            );
          }),
        ];
      }

      return defaultSegmentations;
    }

    return videoSegmentation?.vaData.segmentationDetails?.segmentations.filter(
      (x) => x.accuracy === videoSegmentation?.highlightDetectionOverallConfidence
    );
  }, [
    videoSegmentation?.detectedGroupCodes,
    videoSegmentation?.detectionIndividualConfidenceLevels,
    videoSegmentation?.highlightDetectionConfidenceOption,
    videoSegmentation?.highlightDetectionOverallConfidence,
    videoSegmentation?.isLoading,
    videoSegmentation?.segmentationSuccessful,
    videoSegmentation?.vaData.recommendedConfidenceLevels,
    videoSegmentation?.vaData.segmentationDetails?.segmentations,
  ]);

  const renderSegmentationsBasedOnGroupCode = useCallback(
    (segmentations: Segmentation[], context: CanvasRenderingContext2D) => {
      const color = videoSegmentation?.getGroupCodeColors()[segmentations[0].groupCode.toLowerCase()];

      for (let i = 0; i < segmentations.length; i++) {
        context.beginPath();
        context.lineWidth = videoPlayerService.isFullscreen ? 4 : 2;
        context.strokeStyle = hexToRGBA(color);
        context.fillStyle = hexToRGBA(color, 0.2);

        for (let j = 0; j < segmentations[i].coordinates.length; j++) {
          context.lineTo(
            (segmentations[i].coordinates[j].x * videoPlayerWidth) /
              videoSegmentation?.vaData.segmentationDetails.imageSize.width!,
            (segmentations[i].coordinates[j].y * videoPlayerHeight) /
              videoSegmentation?.vaData.segmentationDetails.imageSize.height!
          );
        }
        context.closePath();

        if (showContour) {
          context.stroke();
        }

        if (showArea) {
          context.fill();
        }

        if (showBox) {
          const minX =
            (Math.min(...segmentations[i].coordinates.map((coord) => coord.x)) * videoPlayerWidth) /
            videoSegmentation?.vaData.segmentationDetails.imageSize.width!;
          const maxX =
            (Math.max(...segmentations[i].coordinates.map((coord) => coord.x)) * videoPlayerWidth) /
            videoSegmentation?.vaData.segmentationDetails.imageSize.width!;
          const minY =
            (Math.min(...segmentations[i].coordinates.map((coord) => coord.y)) *
              videoPlayerHeight) /
            videoSegmentation?.vaData.segmentationDetails.imageSize.height!;
          const maxY =
            (Math.max(...segmentations[i].coordinates.map((coord) => coord.y)) *
              videoPlayerHeight) /
            videoSegmentation?.vaData.segmentationDetails.imageSize.height!;

          context.strokeRect(minX, minY, maxX - minX, maxY - minY);
        }
      }
    },
    [showArea, showBox, showContour, videoSegmentation, videoPlayerHeight, videoPlayerWidth]
  );

  const detectedGroupCodes = JSON.stringify(videoSegmentation?.detectedGroupCodes);

  const renderClosestPredictionBasedOnVideoTime = useCallback(
    (context: CanvasRenderingContext2D, currentVideoTime: number) => {
      context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);

      const CLOSEST_PREDICTION_THRESHOLD = 0.15;
      const closestSegmentations = filteredSegmentations?.filter((segmentation) => {
        const closestTimestamp = filteredSegmentations?.reduce((previous, current) =>
          Math.abs(current.timestamp - currentVideoTime) <
          Math.abs(previous.timestamp - currentVideoTime)
            ? current
            : previous
        )?.timestamp;

        return segmentation.timestamp === closestTimestamp;
      });

      if (
        !closestSegmentations ||
        !closestSegmentations.length ||
        Math.abs(closestSegmentations[0].timestamp - currentVideoTime) >
          CLOSEST_PREDICTION_THRESHOLD
      ) {
        return;
      }

      videoSegmentation?.detectedGroupCodes?.forEach((groupCode) => {
        if (groupCode.isVisible && groupCode.isDetected) {
          const groupCodeClosestSegmentations = closestSegmentations?.filter(
            (s) => s.groupCode === groupCode.code
          );

          if (groupCodeClosestSegmentations?.length) {
            renderSegmentationsBasedOnGroupCode(groupCodeClosestSegmentations, context!);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      renderSegmentationsBasedOnGroupCode,
      videoSegmentation?.getGroupCodeColors,
      detectedGroupCodes,
      videoPlayerHeight,
      videoPlayerWidth,
      filteredSegmentations,
    ]
  );

  useEffect(() => {
    if (!featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations)) return;

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const videoElement = document.querySelector("#my-video > video") as any;

    if (!context || videoSegmentation?.isLoading || !videoElement) return;

    const renderFramePredictions = () => {
      const computedWidth =
        (videoElement?.clientHeight * videoElement.videoWidth) / videoElement.videoHeight;

      setVideoPlayerWidth(computedWidth);
      setVideoPlayerHeight(videoElement?.clientHeight);
      if (videoSegmentation?.segmentationRunning) {
        context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);
        return;
      }

      if (videoSegmentation?.vaData.segmentationDetails && filteredSegmentations) {
        if (!videoPlayerService.isVideoPlaying) {
          renderClosestPredictionBasedOnVideoTime(context, videoPlayerService.videoProgress);
          return;
        }
        renderClosestPredictionBasedOnVideoTime(context, videoElement.currentTime);

        requestId.current = requestAnimationFrame(renderFramePredictions);
      }
    };

    renderFramePredictions();

    return () => {
      cancelAnimationFrame(requestId.current!);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, videoSegmentation?.isLoading, videoSegmentation?.segmentationRunning, videoProgress, showArea, showContour, showBox]);

  console.log(videoSegmentation?.status)
  return (
    <>
      <VideoPlayer>
        {featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) && (
          <canvas
            style={{ position: "absolute" }}
            width={videoPlayerWidth}
            height={videoPlayerHeight}
            ref={canvasRef}
          />
        )}
        <VideoControls
          videoSegmentation={props.videoSegmentation}
          filteredSegmentations={filteredSegmentations}
        />
      </VideoPlayer>
    </>
  );
});
